import { InformationCircleIcon } from "@heroicons/react/20/solid";
import _ from "lodash";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react.tsx";
import { Base, Label } from "@/atoms-react/text/Text.react";
import type { levelBronzeFormDataType } from "@/features/flow/types";
import { useKYC } from "@/features/kyc/hooks/use-kyc";
import { useDocument } from "@/features/user/document/hooks/use-document";
import type { Document } from "@/features/user/document/types";
import { useQuiz } from "@/features/user/quiz/hooks/use-quiz";
import { UserType } from "@/features/user/types";
import { Themes } from "@/types/theme";
import { BronzeFormCareerPath } from "./bronze-form/bronze-form-career-path";
import { CivilState } from "./civil_state.react";
import { MovableIncomes } from "./movable-assets.react";
import { OriginAssets } from "./origin-assets.react";
import { OtherAssetsPeriod } from "./other_ressources_assetes_period.react";
import { OtherSourceIncomes } from "./other-income-source.react";
import { RealEstate } from "./real_estate.react";
import { SourceIncomes } from "./source-income.react";
import { UploadFileV2 } from "./upload-file.v2";
import { ValueOfMovable } from "./value_of_movable_assets.react";

type IdentityNPContentProps = {
  user: UserType | undefined;
  bronzForm: levelBronzeFormDataType;
  allowSubmit: boolean;
  status: number;
  handleEdit: (form: levelBronzeFormDataType) => void;
  handlePourcentage: (p: number) => void;
} & React.ComponentPropsWithoutRef<"div">;

export const LebelQuizBronze = ({
  user,
  allowSubmit,
  handlePourcentage,
  handleEdit,
  bronzForm,
  status,
  ...props
}: IdentityNPContentProps) => {
  const { t, i18n } = useTranslation(["pass"]);
  const { editKycBronzeMutationWithoutDebounce } = useKYC();
  const { mutateAsync: editFormOnSubmit } = editKycBronzeMutationWithoutDebounce();

  const labelInputDivClassName = "md:flex items-center md:space-x-6 mt-4";
  const labelClassName = "flex w-full md:w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex grow overflow-auto";
  const [bronzeFormResponses, setBronzeFormResponses] =
    useState<levelBronzeFormDataType>(bronzForm);
  const bronzeFormResponsesStatic = useRef<levelBronzeFormDataType>(bronzForm);
  const { ItemsAssestQuery } = useQuiz();
  const { data: response, isLoading: isLoadingSourcesIncome } = ItemsAssestQuery();
  const { updateUserUnderAuditMutation } = useDocument();
  const { mutateAsync: submitForm, isPending: loading } = updateUserUnderAuditMutation();

  const handleUpdateStatus = async () => {
    try {
      await editFormOnSubmit(bronzeFormResponses);
      await submitForm();
      window.location.reload();
    } catch (_) {
      toast.error("error");
    }
  };
  function isObject(object) {
    return object != null && typeof object === "object";
  }
  function deepEquals(object1, object2) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    if (keys1.length !== keys2.length) {
      return false;
    }
    for (const key of keys1) {
      const val1 = object1[key];
      const val2 = object2[key];
      const areObjects = isObject(val1) && isObject(val2);
      if ((areObjects && !deepEquals(val1, val2)) || (!areObjects && val1 !== val2)) {
        return false;
      }
    }
    return true;
  }

  useEffect(() => {
    if (status === 3)
      if (
        bronzeFormResponses &&
        bronzeFormResponsesStatic &&
        !deepEquals(bronzeFormResponses, bronzeFormResponsesStatic)
      ) {
        handleEdit(bronzeFormResponses);
        bronzeFormResponsesStatic.current = bronzeFormResponses;
      }
  }, [bronzeFormResponses]);
  function numberWithCommas(x?: string | null) {
    if (x?.length === 0 || x == null) return "0";

    const value = i18n.language === "fr" ? x.replaceAll(".", "") : x.replace(/,/g, "");
    const val = parseFloat(value).toLocaleString("en-US", {
      maximumFractionDigits: 3,
      minimumFractionDigits: 0
    });
    if (i18n.language === "fr") return val.replace(/,/g, ".");
    return val;
  }
  return (
    <>
      {bronzeFormResponses && (
        <div
          className={
            (status as number) >= 4
              ? "pointer-events-none opacity-50"
              : "" + "w-full md:mb-6 2xl:mb-0"
          }
        >
          <div
            className={twMerge("mt-5 p-2 md:px-6 md:py-4 md:pb-6", props.className)}
            {..._.omit(props, "className")}
          >
            <div>
              <Base> {t("olkypass.mainPage.olkypass.BronzeForms.description")} </Base>
            </div>
            {/**Etat civil */}
            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />

            <div className={"mt-4 items-center md:flex md:space-x-1"}>
              <div className="flex flex-row">
                <Label className={labelClassName}>
                  {t("olkypass.mainPage.olkypass.BronzeForms.Proof_of_residence")}
                </Label>
                <InformationCircleIcon
                  className="h-5 w-5 rounded-full bg-primary text-white dark:text-slate-900"
                  data-tooltip={t(
                    "olkypass.mainPage.olkypass.BronzeForms.Proof_of_residence_description"
                  )}
                  data-position="right"
                />
              </div>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                {/*53 is the document type code for address justification*/}
                <UploadFileV2
                  realIdentityUuid={user?.naturalPerson.UUID ?? ""}
                  dataCy="bronze-form-residenceproof-document"
                  documentsType={53}
                  uploadedFile={bronzeFormResponses?.address_justification as string}
                  setResponse={(uploaded: Document) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        address_justification: uploaded?.UUID
                      };
                    });
                  }}
                />
              </div>
            </div>
            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />

            <div className={labelInputDivClassName}>
              <Label className={labelClassName}>
                {t("olkypass.mainPage.olkypass.BronzeForms.civil_state")}
              </Label>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <CivilState
                  civilStateResponse={bronzeFormResponses?.civilState}
                  setResponseCivilState={(cs) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        civilState: cs
                      };
                    });
                  }}
                />
              </div>
            </div>
            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />
            <Base className="mt-3 mb-3 text-slate-300 text-xl">
              {t("olkypass.mainPage.olkypass.BronzeForms.monthly_income")}
            </Base>
            {/** Categorie*/}
            <div className={labelInputDivClassName}>
              <Label className={labelClassName}>
                {t("olkypass.mainPage.olkypass.BronzeForms.Categories")}
              </Label>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <SourceIncomes
                  isLoadingSourcesIncome={isLoadingSourcesIncome}
                  sourceIncomeItems={response?.data
                    ?.sort()
                    .filter(
                      (incomeSelection) =>
                        incomeSelection.code === 11 ||
                        incomeSelection.code === 15 ||
                        incomeSelection.code === 14
                    )}
                  sourceIncomes={bronzeFormResponses?.assets_category as string[]}
                  setFormResponseSI={(asset, checked) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        assets_category: checked
                          ? [
                              ...((prevState?.assets_category as string[]) ?? []),
                              asset.code.toString()
                            ]
                          : prevState?.assets_category &&
                            prevState?.assets_category.filter(
                              (code: string) => code !== asset.code.toString()
                            )
                      };
                    });
                  }}
                />
              </div>
            </div>

            <div className={labelInputDivClassName}>
              <Label className={labelClassName}>
                {t("olkypass.mainPage.olkypass.BronzeForms.other_ressources")}
              </Label>

              <div className={twMerge("relative space-x-3", inputClassName)}>
                <OtherSourceIncomes
                  isLoadingSourcesIncome={isLoadingSourcesIncome}
                  sourceIncomeItems={response?.data
                    ?.sort()
                    .filter(
                      (incomeSelection) =>
                        incomeSelection.code === 19 ||
                        incomeSelection.code === 13 ||
                        incomeSelection.code === 12 ||
                        incomeSelection.code === 9 ||
                        incomeSelection.code === 10
                    )}
                  othersourceIncomes={bronzeFormResponses?.other_assets_category as string[]}
                  commentProps={bronzeFormResponses?.other_assets_category_comment as string}
                  setFormResponseOSI={(asset, checked) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        other_assets_category: checked
                          ? [...prevState.other_assets_category, asset.code.toString()]
                          : [
                              ...prevState.other_assets_category.filter((code: string) => {
                                return code !== asset.code.toString();
                              })
                            ],
                        other_assets_period:
                          !checked && prevState.other_assets_category.length === 1
                            ? null
                            : prevState.other_assets_period,
                        other_assets_annual_amount:
                          !checked && prevState.other_assets_category.length === 1
                            ? null
                            : prevState.other_assets_annual_amount,
                        other_assets_category_comment:
                          !checked && asset.code.toString() === "10"
                            ? undefined
                            : prevState.other_assets_category_comment
                      };
                    });
                  }}
                  setFormResponseOSIComment={(comment, otherChecked) =>
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        other_assets_category_comment: otherChecked ? comment : null
                      };
                    })
                  }
                />
              </div>
            </div>
            {/**Ces ressources sont */}
            {bronzeFormResponses.other_assets_category.length > 0 && (
              <div className={labelInputDivClassName}>
                <Label className={labelClassName}>
                  {t("olkypass.mainPage.olkypass.BronzeForms.other_ressources_part2")}
                </Label>
                <div className={twMerge("relative space-x-3", inputClassName)}>
                  <OtherAssetsPeriod
                    otherAssetsPeriodResponse={
                      bronzeFormResponses?.other_assets_period
                        ? (bronzeFormResponses?.other_assets_period as string)
                        : undefined
                    }
                    setResponseotherAssetsPeriodState={(oap) => {
                      setBronzeFormResponses((prevState) => {
                        return {
                          ...prevState,
                          other_assets_period: oap
                        };
                      });
                    }}
                  />
                </div>
              </div>
            )}
            {bronzeFormResponses.other_assets_category.length > 0 && (
              <div className={labelInputDivClassName}>
                <Label className={labelClassName}>
                  {bronzeFormResponses.other_assets_period === "Recurring"
                    ? t("olkypass.mainPage.olkypass.BronzeForms.othet_ressource_value_regular")
                    : t("olkypass.mainPage.olkypass.BronzeForms.othet_ressource_value")}
                </Label>
                <div className={twMerge("relative space-x-3", inputClassName)}>
                  <InputLabel
                    value={
                      bronzeFormResponses?.other_assets_annual_amount === null
                        ? undefined
                        : numberWithCommas(bronzeFormResponses?.other_assets_annual_amount)
                    }
                    suffixIcon={<span>€</span>}
                    onChange={(value: string) => {
                      const v =
                        i18n.language === "fr"
                          ? value.replaceAll(".", "")
                          : value.replace(/,/g, "");

                      if (isNaN(+v)) return;
                      setBronzeFormResponses((prevState) => {
                        return {
                          ...prevState,
                          other_assets_annual_amount: v
                        };
                      });
                    }}
                    className="mb-1 flex-1 md:mb-0"
                  />
                </div>
              </div>
            )}

            {/**Valeur estimée des biens immobilier */}
            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />
            <Base className="mt-3 mb-3 text-slate-300 text-xl">
              {t("olkypass.mainPage.olkypass.BronzeForms.property_assets")}
            </Base>

            <div className={labelInputDivClassName}>
              <Label className={labelClassName}>
                {t("olkypass.mainPage.olkypass.BronzeForms.real_estate_label")}
              </Label>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <RealEstate
                  realestateResponse={bronzeFormResponses?.real_estate_assets}
                  setResponseRealEstate={(re) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        real_estate_assets: re,
                        value_real_estate:
                          re === "I own one or more properties"
                            ? (bronzeFormResponses?.value_real_estate?.trim() ?? "0")
                            : "0"
                      };
                    });
                  }}
                />
              </div>
            </div>

            {bronzeFormResponses?.real_estate_assets === "I own one or more properties" && (
              <div className={labelInputDivClassName}>
                <Label className={labelClassName}>
                  {t("olkypass.mainPage.olkypass.BronzeForms.estimated_value_real_estate")}
                </Label>
                <div className={twMerge("relative space-x-3", inputClassName)}>
                  <InputLabel
                    value={
                      bronzeFormResponses?.value_real_estate === null
                        ? undefined
                        : numberWithCommas(bronzeFormResponses?.value_real_estate)
                    }
                    suffixIcon={<span>€</span>}
                    onChange={(value: string) => {
                      const v =
                        i18n.language === "fr"
                          ? value.replaceAll(".", "")
                          : value.replace(/,/g, "");
                      if (isNaN(+v)) return;
                      setBronzeFormResponses((prevState) => {
                        return {
                          ...prevState,
                          value_real_estate: v
                        };
                      });
                    }}
                    className="mb-1 flex-1 md:mb-0"
                  />
                </div>
              </div>
            )}

            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />
            <Base className="mt-3 mb-3 text-slate-300 text-xl">
              {t("olkypass.mainPage.olkypass.BronzeForms.other_real_estate")}
            </Base>
            <div className={labelInputDivClassName}>
              <Label className={labelClassName}>
                {t("olkypass.mainPage.olkypass.BronzeForms.estimation_property_assets")}
              </Label>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <MovableIncomes
                  isLoadingSourcesIncome={isLoadingSourcesIncome}
                  sourceIncomeItems={response?.data
                    ?.sort()
                    .filter(
                      (incomeSelection) =>
                        incomeSelection.code === 1 ||
                        incomeSelection.code === 21 ||
                        incomeSelection.code === 22 ||
                        incomeSelection.code === 23 ||
                        incomeSelection.code === 20 ||
                        incomeSelection.code === 9 ||
                        incomeSelection.code === 10
                    )}
                  mouvableIncomes={bronzeFormResponses?.other_real_estate_assets as string[]}
                  commentProps={bronzeFormResponses?.other_real_estate_assets_comment as string}
                  setFormResponseMI={(asset, checked) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        other_real_estate_assets: checked
                          ? [
                              ...(prevState?.other_real_estate_assets as string[]),
                              asset.code.toString()
                            ]
                          : prevState?.other_real_estate_assets &&
                            prevState?.other_real_estate_assets.filter(
                              (code: string) => code !== asset.code.toString()
                            ),
                        other_real_estate_assets_estimation:
                          !checked && prevState.other_real_estate_assets.length === 1
                            ? {
                                maximum: null,
                                minimum: null
                              }
                            : prevState.other_real_estate_assets_estimation,

                        other_real_estate_assets_comment:
                          !checked &&
                          asset.code.toString() === "10" &&
                          prevState.other_real_estate_assets_comment
                            ? undefined
                            : prevState && prevState.other_real_estate_assets_comment
                              ? prevState.other_real_estate_assets_comment
                              : undefined
                      };
                    });
                  }}
                  setFormResponseOSIComment={(comment, otherChecked) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        other_real_estate_assets_comment: otherChecked ? comment : null
                      };
                    });
                  }}
                />
              </div>
            </div>
            {/**Valeur totale estimée du patrimoine mobilier */}
            {bronzeFormResponses.other_real_estate_assets.length > 0 && (
              <div className={labelInputDivClassName}>
                <Label className={labelClassName}>
                  {t("olkypass.mainPage.olkypass.BronzeForms.total_estimation_movable_assets")}
                </Label>
                <div className={twMerge("relative space-x-3", inputClassName)}>
                  <ValueOfMovable
                    valueOfMovable={
                      bronzeFormResponses?.other_real_estate_assets_estimation as {
                        minimum: number;
                        maximum: number | null;
                      }
                    }
                    setResponseMovableIncome={(mi: { minimum: number; maximum: number | null }) => {
                      setBronzeFormResponses((prevState) => ({
                        ...prevState,
                        other_real_estate_assets_estimation: mi
                      }));
                    }}
                  />
                </div>
              </div>
            )}
            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />
            <Base className="mt-3 mb-3 text-slate-300 text-xl">
              {t("olkypass.mainPage.olkypass.BronzeForms.debts")}
            </Base>

            <div className={labelInputDivClassName}>
              <Label className={twMerge(labelClassName)}>
                {t("olkypass.mainPage.olkypass.BronzeForms.debts_estimaion")}
              </Label>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <InputLabel
                  value={
                    bronzeFormResponses?.debt === null
                      ? undefined
                      : numberWithCommas(bronzeFormResponses?.debt)
                  }
                  suffixIcon={<span>€</span>}
                  data-tooltip={t("olkypass.mainPage.olkypass.BronzeForms.input_tooltip")}
                  data-position="bottom"
                  onChange={(value: string) => {
                    const v =
                      i18n.language === "fr" ? value.replaceAll(".", "") : value.replace(/,/g, "");
                    if (isNaN(+v)) return;
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        debt: v
                      };
                    });
                  }}
                  className="mb-1 flex-1 md:mb-0"
                />
              </div>
            </div>

            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />
            <Base className="mt-3 mb-3 text-slate-300 text-xl">
              {t("olkypass.mainPage.olkypass.BronzeForms.origins_assets")}
            </Base>
            {/**Origin Assets */}
            <div className={labelInputDivClassName}>
              <Label className={labelClassName}>
                {t("olkypass.mainPage.olkypass.BronzeForms.origins_assets_label")}
              </Label>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <OriginAssets
                  isLoadingSourcesIncome={isLoadingSourcesIncome}
                  sourceIncomeItems={response?.data
                    ?.sort()
                    .filter(
                      (incomeSelection) =>
                        incomeSelection.code === 25 ||
                        incomeSelection.code === 26 ||
                        incomeSelection.code === 27 ||
                        incomeSelection.code === 5 ||
                        incomeSelection.code === 6 ||
                        incomeSelection.code === 10 ||
                        incomeSelection.code === 13 ||
                        incomeSelection.code === 4
                    )}
                  originAssetsProps={bronzeFormResponses?.origin_assets_category as string[]}
                  commentProps={bronzeFormResponses?.origin_assets_comment as string}
                  setFormResponseOOA={(asset, checked) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        origin_assets_category: checked
                          ? [
                              ...(prevState?.origin_assets_category as string[]),
                              asset.code.toString()
                            ]
                          : prevState?.origin_assets_category &&
                            prevState?.origin_assets_category.filter(
                              (code: string) => code !== asset.code.toString()
                            ),
                        origin_assets_comment:
                          !checked &&
                          asset.code.toString() === "10" &&
                          prevState.origin_assets_comment
                            ? null
                            : prevState && prevState.origin_assets_comment
                              ? prevState.origin_assets_comment
                              : null
                      };
                    });
                  }}
                  setFormResponseOOAComment={(comment, otherChecked) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        origin_assets_comment: otherChecked ? comment : null
                      };
                    });
                  }}
                />
              </div>
            </div>
            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />
            <Base className="mt-3 mb-3 text-slate-300 text-xl">
              {t("olkypass.mainPage.olkypass.BronzeForms.Comments")}
            </Base>
            <div className={labelInputDivClassName}>
              <Label className={labelClassName}>
                {t("olkypass.mainPage.olkypass.BronzeForms.Comments_assets")}
              </Label>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <InputLabel
                  value={bronzeFormResponses?.comment_assets as string}
                  onChange={(value: string) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        comment_assets: value
                      };
                    });
                  }}
                  className="mb-1 flex-1 md:mb-0"
                  rows={2}
                />
              </div>
            </div>
            <hr className="my-8 mt-12 h-px border-0 bg-gray-200 dark:bg-slate-400" />
            {/* Career Path */}
            <BronzeFormCareerPath
              realIdentityUuid={user?.naturalPerson.UUID ?? ""}
              setBronzeFormState={setBronzeFormResponses}
              bronzeFormState={bronzeFormResponses}
            />
            {/*  */}
            <div className={labelInputDivClassName}>
              <div className={twMerge("relative space-x-3", inputClassName)}>
                <Checkbox
                  dataCy="bronze-form-cgu-checkbox"
                  value={bronzeFormResponses?.honor_declaration}
                  onChange={(value) => {
                    setBronzeFormResponses((prevState) => {
                      return {
                        ...prevState,
                        honor_declaration: value
                      };
                    });
                  }}
                >
                  <>
                    <Base className="mb-4 cursor-pointer">
                      {t("olkypass.mainPage.olkypass.BronzeForms.honor_declaration_1")}
                    </Base>
                    <Base className="mb-1 cursor-pointer">
                      {t("olkypass.mainPage.olkypass.BronzeForms.honor_declaration_2")}
                    </Base>
                  </>
                </Checkbox>
              </div>
            </div>
            <div className="mt-10 flex flex-row">
              <Button
                className="w-52 w-max shrink-0"
                dataCy="bronze-form-validate-button"
                size="lg"
                onClick={() => handleUpdateStatus()}
                theme={Themes.primary}
                disabled={!allowSubmit || status !== 3 || !bronzeFormResponses.honor_declaration}
                loading={loading}
              >
                {t("olkypass.mainPage.olkypass.BronzeForms.validate_button")}
              </Button>
              <div className="ml-4 flex flex-col justify-center">
                {/**  <div className="flex flex-row ">
                  <ExclamationTriangleIcon className="text-primary h-6 w-6" />
                  <Base className="text-base">
                    {' '}
                    {t('olkypass.mainPage.olkypass.BronzeForms.required')}
                  </Base>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
